import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  FileText, 
  User, 
  Calendar,
  Download,
  Send,
  ArrowLeft,
  CheckCircle
} from "lucide-react";

const LetterOfEngagement = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [formData, setFormData] = useState({
    // Client Information
    clientName: "",
    serviceType: "",
    
    // Scope of Services
    scopeDetails: "",
    
    // Fees
    consultationFee: "",
    serviceFee: "",
    governmentCharges: "",
    
    // Dates
    engagementDate: new Date().toISOString().split('T')[0],
    validityDays: "10",
    
    // Client Acceptance
    clientSignature: "",
    clientDate: "",
    
    // Status
    status: "draft" // draft, sent, signed
  });

  const serviceTypes = [
    "Trade License Registration",
    "Business Setup & Formation", 
    "VAT Registration & Services",
    "Sports Management Consultancy",
    "Event Management",
    "HR & Compliance Services",
    "Corporate Training",
    "Legal Advisory",
    "Other"
  ];

  // Fetch client data if client_id is provided
  useEffect(() => {
    if (clientId) {
      fetchClientData(clientId);
    }
  }, [clientId]);

  const fetchClientData = async (id: string) => {
    try {
      const response = await fetch(`https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_management_20251222_1800?client_id=${id}`, {
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const client = data.client;
        
        // Auto-populate client information
        setFormData(prev => ({
          ...prev,
          clientName: client.full_name
        }));

        toast({
          title: "Client Data Loaded",
          description: `LOE form populated with ${client.full_name}'s information`,
        });
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast({
        title: "Error",
        description: "Failed to load client data",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateLetter = () => {
    const letterContent = `
ELITE EDGE MANAGEMENT CONSULTANCY

LETTER OF ENGAGEMENT

Initial Consultation & Administrative Support in ${formData.serviceType}

Between

Elite Edge Management Consultancy, Alshmookh Business Centre, UAE

&

${formData.clientName}

1. Purpose of Engagement

This Letter of Engagement ("Letter") confirms the initial understanding between Elite Edge Management Consultancy ("Consultant") and ${formData.clientName} ("Client") whereby the Consultant shall provide consultation services and administrative support to assist the Client in ${formData.serviceType}.

This Letter represents an initial and preliminary engagement and does not constitute a full service or long-term agreement.

2. Scope of Initial Services

The Consultant shall provide initial advisory and administrative support including, but not limited to:

${formData.scopeDetails}

Any services beyond the above scope shall be subject to separate written confirmation or agreement.

3. Fees & Payment Milestones

Fees and official charges shall be communicated and agreed. Unless otherwise agreed in writing, payment shall be structured as follows:

1— All government, Free Zone, and third-party charges shall be borne by the Client.

2— Elite Edge Management consultancy fees:
- 50% Initial consultation & engagement fee - payable upon acceptance of this Letter: AED ${formData.consultationFee}
- 50% Balance of service fees (if any) – payable upon completion of agreed services: AED ${formData.serviceFee}

4. Confidentiality

Both Parties agree to treat all business, financial, and personal information exchanged under this engagement as confidential and to use such information solely for the purpose of this engagement.

5. Non-Circumvention

The Client agrees not to directly or indirectly bypass, circumvent, or engage with any Free Zone authority, government entity, third-party service provider, or contact introduced by the Consultant for the purpose of avoiding payment of fees or commissions related to this engagement, for a period of twelve (12) months from the date of this Letter.

6. No Guarantee

The Consultant shall use reasonable professional efforts; however, no guarantee is given regarding approvals, timelines, or outcomes, as these are subject to the regulations and decisions of the relevant authorities.

7. Term

This Letter shall be valid for ${formData.validityDays} days from the date above, unless replaced by a formal service agreement or terminated by written notice from either Party.

8. Governing Law

This Letter shall be governed by and construed in accordance with the laws of the United Arab Emirates, as applicable in the Emirate of Dubai.

9. Acceptance

By signing below, both Parties confirm their acceptance of this Letter of Engagement.

For Elite Edge Management Consultancy

Name: Areeg Osman
Title: Director of Public Relations and Administration
Date: ${formData.engagementDate}

Client:
Name: ${formData.clientName}
Signature: _______________
Date: ${formData.clientDate}

Contact: info@eliteedgemc.ae | +971585687738
    `;

    return letterContent;
  };

  const handlePreview = () => {
    if (!formData.clientName || !formData.serviceType) {
      toast({
        title: "Missing Information",
        description: "Please fill in client name and service type.",
        variant: "destructive"
      });
      return;
    }

    const letterContent = generateLetter();
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Letter of Engagement - ${formData.clientName}</title>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; }
              h1 { text-align: center; color: #2563eb; }
              h2 { color: #1e40af; margin-top: 30px; }
              .header { text-align: center; margin-bottom: 40px; }
              .content { white-space: pre-line; }
              .signature-section { margin-top: 40px; }
            </style>
          </head>
          <body>
            <div class="content">${letterContent}</div>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleSave = () => {
    toast({
      title: "Letter Saved",
      description: "Letter of Engagement has been saved to CRM system.",
    });
    console.log("Letter of Engagement Data:", formData);
  };

  const handleSendEmail = () => {
    if (!formData.clientName || !formData.serviceType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields before sending.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Email Sent",
      description: `Letter of Engagement sent to client and notification sent to info@eliteedgemc.ae`,
    });
    
    setFormData(prev => ({ ...prev, status: "sent" }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <span className="text-sm text-muted-foreground">Welcome, Admin</span>
              <Button variant="outline" asChild>
                <Link to="/crm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to CRM
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">CRM Document Management</Badge>
          <h1 className="text-4xl font-bold mb-4">Letter of Engagement</h1>
          <p className="text-xl text-muted-foreground">
            Generate and manage client engagement letters
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Form Section */}
            <div className="space-y-6">
              
              {/* Client Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Client Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Client Name *</Label>
                    <Input
                      id="clientName"
                      value={formData.clientName}
                      onChange={(e) => handleInputChange("clientName", e.target.value)}
                      placeholder="Enter client full name or company name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serviceType">Service Type *</Label>
                    <Select value={formData.serviceType} onValueChange={(value) => handleInputChange("serviceType", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((service) => (
                          <SelectItem key={service} value={service}>{service}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Scope of Services */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Scope of Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label htmlFor="scopeDetails">Service Details</Label>
                    <Textarea
                      id="scopeDetails"
                      value={formData.scopeDetails}
                      onChange={(e) => handleInputChange("scopeDetails", e.target.value)}
                      placeholder="• Initial consultation and assessment&#10;• Document preparation and review&#10;• Government liaison and submission&#10;• Follow-up and status updates"
                      rows={6}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Fees Structure */}
              <Card>
                <CardHeader>
                  <CardTitle>Fees Structure</CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="consultationFee">Initial Consultation Fee (AED)</Label>
                    <Input
                      id="consultationFee"
                      value={formData.consultationFee}
                      onChange={(e) => handleInputChange("consultationFee", e.target.value)}
                      placeholder="e.g., 2,500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serviceFee">Service Fee Balance (AED)</Label>
                    <Input
                      id="serviceFee"
                      value={formData.serviceFee}
                      onChange={(e) => handleInputChange("serviceFee", e.target.value)}
                      placeholder="e.g., 2,500"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="governmentCharges">Government/Third-party Charges</Label>
                    <Input
                      id="governmentCharges"
                      value={formData.governmentCharges}
                      onChange={(e) => handleInputChange("governmentCharges", e.target.value)}
                      placeholder="To be borne by client as per actual"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Dates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Engagement Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="engagementDate">Engagement Date</Label>
                    <Input
                      id="engagementDate"
                      type="date"
                      value={formData.engagementDate}
                      onChange={(e) => handleInputChange("engagementDate", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="validityDays">Validity (Days)</Label>
                    <Input
                      id="validityDays"
                      value={formData.validityDays}
                      onChange={(e) => handleInputChange("validityDays", e.target.value)}
                      placeholder="10"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions Section */}
            <div className="space-y-6">
              
              {/* Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Document Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <Badge variant={
                      formData.status === 'draft' ? 'secondary' : 
                      formData.status === 'sent' ? 'default' : 'success'
                    }>
                      {formData.status === 'draft' && 'Draft'}
                      {formData.status === 'sent' && 'Sent to Client'}
                      {formData.status === 'signed' && 'Signed & Completed'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={handlePreview} className="w-full" variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Preview Letter
                  </Button>
                  
                  <Button onClick={handleSave} className="w-full" variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Save to CRM
                  </Button>
                  
                  <Button 
                    onClick={handleSendEmail} 
                    className="w-full bg-gradient-to-r from-primary to-primary-glow"
                    disabled={!formData.clientName || !formData.serviceType}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send via Email
                  </Button>
                </CardContent>
              </Card>

              {/* Client Signature Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Client Acceptance</CardTitle>
                  <CardDescription>
                    To be filled when client accepts the engagement
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientDate">Client Signature Date</Label>
                    <Input
                      id="clientDate"
                      type="date"
                      value={formData.clientDate}
                      onChange={(e) => handleInputChange("clientDate", e.target.value)}
                    />
                  </div>
                  
                  {formData.clientDate && (
                    <Button 
                      onClick={() => setFormData(prev => ({ ...prev, status: "signed" }))}
                      className="w-full"
                      variant="default"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Signed & Complete
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Template Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Template Information</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  <p>This letter template includes:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Standard engagement terms</li>
                    <li>Confidentiality clauses</li>
                    <li>Non-circumvention agreement</li>
                    <li>UAE legal compliance</li>
                    <li>Payment milestone structure</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LetterOfEngagement;